import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button } from 'components/button/Button';
import { CSSProperties, MouseEventHandler, ReactNode } from 'react';
import styled from 'styled-components';
import { tokens } from 'styles/tokens';
import { PickForStyled } from 'utils/types';

const horizontalPadding = '0.5rem';
const verticalPadding = '0.25rem';

type LozengeContainerProps = PickForStyled<
    LozengeProps,
    'isBold' | 'color' | 'backgroundColor'
>;

const LozengeContainer = styled.div<LozengeContainerProps>`
    display: inline-flex;
    align-items: stretch;
    border-radius: 3px;
    box-sizing: border-box;
    font-size: 1rem;
    line-height: 1.4rem;
    font-weight: ${({ $isBold }) => ($isBold ? '500' : 'initial')};
    color: ${({ $color }) => ($color ? $color : '')};
    background-color: ${({ $backgroundColor }) => `${$backgroundColor}`};

    gap: 0.5rem;
`;

type ContentContainerProps = {
    $hasTrailer: boolean;
};

const ContentContainer = styled.div<ContentContainerProps>`
    padding: ${verticalPadding} ${horizontalPadding};
    ${({ $hasTrailer }) => ($hasTrailer ? 'padding-right: 0;' : '')}
    box-sizing: border-box;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    white-space: nowrap;
`;

const RemoveButton = styled(Button)`
    padding: 0;

    padding-inline: ${horizontalPadding};

    &:hover,
    &:focus {
        background-color: ${tokens.lozenge.removeBackgroundHover};
        * {
            color: ${tokens.lozenge.removeIconHover};
        }
    }
`;

export type LozengeProps = {
    id?: string;
    /**
     * Optional title (also used as ARIA Label)
     */
    title?: string;
    /**
     * Background color of the Lozenge
     */
    backgroundColor: string;
    /**
     * Color of the Lozenge text
     */
    color?: string;
    /**
     * Content of the Lozenge
     */
    children: ReactNode;
    /**
     * Whether the text is bolded or not
     */
    isBold?: boolean;
    /**
     * Optional classname to apply to the Lozenge
     */
    className?: string;
    /**
     * Optional styles to apply to the Lozenge
     */
    style?: CSSProperties;
    /**
     * Optional callback for when the Lozenge is clicked on
     */
    onClick?: MouseEventHandler<HTMLDivElement>;
    /**
     * Optional callback for removing the Lozenge
     */
    onRemove?: () => void;
};

export function Lozenge({
    id,
    className,
    style,
    title,
    color,
    backgroundColor,
    children,
    isBold,
    onClick,
    onRemove,
}: LozengeProps) {
    return (
        <LozengeContainer
            id={id}
            className={`${className ?? ''} ${
                onClick ? 'cursor-pointer' : 'cursor-default'
            }`}
            style={style}
            title={title}
            aria-label={title}
            $color={color}
            $backgroundColor={backgroundColor}
            $isBold={isBold}
            onClick={
                onClick
                    ? (event) => {
                          event.stopPropagation();
                          event.preventDefault();
                          onClick(event);
                      }
                    : undefined
            }
        >
            <ContentContainer $hasTrailer={!!onRemove}>{children}</ContentContainer>
            {onRemove && (
                <RemoveButton
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                    }}
                >
                    <FontAwesomeIcon icon={faXmark} color={tokens.icon.primary} />
                </RemoveButton>
            )}
        </LozengeContainer>
    );
}
